import { PriceSheetKind, ProposedPriceLine } from "./priceList";
import { signalHandOff } from "../hooks/useHandOffSignal";

/**
 * A price list handed from Ask or search to a sheet page (#30): the page
 * opens its "Add from a price list" drawer with it. Held in memory only —
 * a reload drops it, and nothing is stored.
 */
export interface PendingPriceList {
    sheet: PriceSheetKind;
    recordId: number;
    text?: string;
    file?: File;
    /** Lines already read (Ask's AI read of the photo): the sheet's reader shows them, no second read. */
    lines?: ProposedPriceLine[];
    /** Where the rates apply (Ask's creation conversation): every line the sheet reads goes in each place. */
    places?: string[];
}

let pending: PendingPriceList | null = null;

export const handOverPriceList = (list: PendingPriceList): void => {
    pending = list;
    // a sheet already on screen takes it now
    signalHandOff();
};

/**
 * A list handed over for a record not created yet (record id 0 — Ask's "new
 * provider ABC" for a price list) moves to the record once it's saved.
 * @returns Whether a list was waiting for it.
 */
export const rekeyPriceList = (sheet: PriceSheetKind, fromId: number, toId: number): boolean => {
    if (!pending || pending.sheet !== sheet || pending.recordId !== fromId) return false;
    pending = { ...pending, recordId: toId };
    return true;
};

/** The list waiting for this sheet, once (it's cleared when taken). */
export const takePriceList = (sheet: PriceSheetKind, recordId: number | null | undefined): PendingPriceList | null => {
    if (!pending || pending.sheet !== sheet || pending.recordId !== recordId) return null;
    const list = pending;
    pending = null;
    return list;
};

/** Fired to open "Read a price list into…" (from Ask's paperclip or a search row). */
export const START_PRICE_LIST_EVENT = "crx:start-price-list";

/** Open the picker that sends a price list to a sheet, optionally with the file already chosen. */
export const startPriceList = (detail: { file?: File; text?: string; lines?: ProposedPriceLine[] } = {}): void => {
    window.dispatchEvent(new CustomEvent(START_PRICE_LIST_EVENT, { detail }));
};
